import React, { useEffect, useState } from 'react';
import { FaTrash, FaUpload } from 'react-icons/fa';
import api from '../services/apiService';
import { ACADEMY_ADMIN_API } from '../utils/academyAccess';

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];

const fileExtension = (name) => {
  const parts = String(name || '').toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : '';
};

const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getDocumentUrl = (filePath) => {
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  return `${base}/${String(filePath || '').replace(/^\//, '')}`;
};

const AcademyUploads = ({ academyId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`${ACADEMY_ADMIN_API}/get_academy_documents.php?id=${academyId}`);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Could not load uploads');
      }
      setDocuments(response.data.documents || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not load uploads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [academyId]);

  const handleUpload = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    const nextTitle = title.trim();
    const nextDescription = description.trim();
    if (!nextTitle) {
      setError('Add a title.');
      return;
    }
    if (nextDescription.length > 500) {
      setError('Short description must be 500 characters or fewer.');
      return;
    }
    if (!file) {
      setError('Choose a PDF, JPG or PNG file.');
      return;
    }
    const extension = fileExtension(file.name);
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setError('Only PDF, JPG and PNG files are allowed.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Files must be 5MB or smaller.');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('id', academyId);
      formData.append('title', nextTitle);
      formData.append('description', nextDescription);
      formData.append('file', file);
      const response = await api.post(`${ACADEMY_ADMIN_API}/upload_academy_document.php`, formData, {
        timeout: 60000,
      });
      if (!response.data.success || !response.data.document) {
        throw new Error(response.data.message || 'Could not upload the file');
      }
      setDocuments((current) => [response.data.document, ...current]);
      setTitle('');
      setDescription('');
      setFile(null);
      setFileKey((current) => current + 1);
      setSuccess('File uploaded. It is now listed on Team Information.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not upload the file');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (documentItem) => {
    if (!window.confirm(`Delete "${documentItem.title}"? It will be removed from Team Information.`)) {
      return;
    }
    setDeletingId(documentItem.document_id);
    setError('');
    setSuccess('');
    try {
      const response = await api.post(`${ACADEMY_ADMIN_API}/delete_academy_document.php`, {
        id: academyId,
        document_id: documentItem.document_id,
      });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Could not delete the file');
      }
      setDocuments((current) => current.filter((row) => row.document_id !== documentItem.document_id));
      setSuccess('File deleted.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not delete the file');
    } finally {
      setDeletingId(0);
    }
  };

  return (
    <div className="settings-form">
      <h3 className="tab-title">Uploads</h3>
      <p className="hint">
        Upload club files such as safeguarding policies. PDF, JPG and PNG only, up to 5MB each.
        These files are listed on the Team Information page for every team assigned to this academy.
      </p>
      {error ? <div className="alert alert-error">{error}</div> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}
      <form onSubmit={handleUpload}>
        <label className="field">
          Title
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={255}
            required
          />
        </label>
        <label className="field">
          Short description (optional)
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Optional"
          />
        </label>
        <label className="field">
          File
          <span className="file-picker">
            <input
              key={fileKey}
              className="file-picker-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
            />
            <span className="file-picker-button">Choose file</span>
            <span className="file-picker-name">{file ? file.name : 'PDF, JPG or PNG, up to 5MB'}</span>
          </span>
        </label>
        <button className="save-button" type="submit" disabled={saving}>
          <FaUpload aria-hidden="true" /> {saving ? 'Uploading…' : 'Upload file'}
        </button>
      </form>
      {loading ? (
        <p className="hint">Loading uploads…</p>
      ) : documents.length === 0 ? (
        <p className="hint">No files have been uploaded yet.</p>
      ) : (
        <ul className="upload-list">
          {documents.map((documentItem) => {
            const deleting = deletingId === documentItem.document_id;
            return (
              <li key={documentItem.document_id}>
                <div className="upload-main">
                  <strong>{documentItem.title}</strong>
                  {documentItem.description ? <small>{documentItem.description}</small> : null}
                  <small>
                    {String(documentItem.file_type || '').toUpperCase()}
                    {documentItem.file_size ? ` · ${formatFileSize(documentItem.file_size)}` : ''}
                  </small>
                </div>
                <div className="upload-actions">
                  <a
                    className="text-button"
                    href={getDocumentUrl(documentItem.file_path)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View
                  </a>
                  <button
                    type="button"
                    className="text-button is-danger"
                    onClick={() => handleDelete(documentItem)}
                    disabled={deleting}
                  >
                    <FaTrash aria-hidden="true" /> {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AcademyUploads;
